import { useState, useCallback, useMemo, useEffect } from 'react';
import type { CreateTaskInput, CreateLinkedTasksInput } from '@flote/types/delivery';
import type { Recipient, Destination, B2BWaveOrderSummary, Driver } from '@flote/contracts/http';
import type { TaskCreationType } from '../TaskTypeSelector';

export type TaskFormData =
  | { type: 'single'; data: CreateTaskInput }
  | { type: 'linked'; data: CreateLinkedTasksInput };

const NO_TEAM_VALUE = '__none__';
const NO_ORDER_VALUE = '__none__';
const NO_DRIVER_VALUE = '__none__';

// Toggle this to true when ready for production
const ORDER_REQUIRED = false;

export interface UseTaskFormOptions {
  recipients: Recipient[];
  destinations: Destination[];
  orders: B2BWaveOrderSummary[];
  onFetchDriversByTeam?: (teamId: string) => Promise<Driver[]>;
}

export interface AddressState {
  search: string;
  streetNumber: string;
  streetAddress: string;
  apartment: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface RecipientState {
  name: string;
  phone: string;
}

export interface TimeWindowState {
  completeAfter: string;
  completeBefore: string;
}

export interface CompletionRequirementsState {
  requireSignature: boolean;
  requirePhoto: boolean;
  requireNotes: boolean;
}

export interface PickupState {
  // Team/Driver assignment
  selectedTeamId: string;
  selectedDriverId: string;
  autoAssign: boolean;
  // Recipient SelectOrCreate
  selectedRecipientId: string | undefined;
  recipientMode: 'select' | 'create';
  recipient: RecipientState;
  // Destination SelectOrCreate
  selectedDestinationId: string | undefined;
  destinationMode: 'select' | 'create';
  address: AddressState;
  // Other fields
  notes: string;
  timeWindow: TimeWindowState;
  completionRequirements: CompletionRequirementsState;
}

export interface DropoffState {
  selectedOrderId: string;
  selectedTeamId: string;
  selectedDriverId: string;
  autoAssign: boolean;
  selectedRecipientId: string | undefined;
  recipientMode: 'select' | 'create';
  selectedDestinationId: string | undefined;
  destinationMode: 'select' | 'create';
  address: AddressState;
  recipient: RecipientState;
  notes: string;
  timeWindow: TimeWindowState;
  completionRequirements: CompletionRequirementsState;
}

export interface UseTaskFormReturn {
  // Task type
  taskType: TaskCreationType;
  setTaskType: (type: TaskCreationType) => void;

  // Pickup state
  pickup: PickupState;
  setPickupTeamId: (id: string) => void;
  setPickupDriverId: (id: string) => void;
  setPickupAutoAssign: (autoAssign: boolean) => void;
  setPickupRecipientId: (id: string | undefined) => void;
  setPickupRecipientMode: (mode: 'select' | 'create') => void;
  setPickupRecipient: (field: keyof RecipientState, value: string) => void;
  setPickupDestinationId: (id: string | undefined) => void;
  setPickupDestinationMode: (mode: 'select' | 'create') => void;
  setPickupAddress: (field: keyof AddressState, value: string) => void;
  handlePickupAddressSelect: (address: AddressState) => void;
  setPickupNotes: (notes: string) => void;
  setPickupTimeWindow: (field: keyof TimeWindowState, value: string) => void;
  setPickupCompletionRequirements: (field: keyof CompletionRequirementsState, value: boolean) => void;
  // Pickup computed
  pickupDrivers: Driver[];
  isLoadingPickupDrivers: boolean;
  isNewPickupRecipient: boolean;
  isNewPickupDestination: boolean;
  selectedPickupRecipient: Recipient | undefined;
  selectedPickupDestination: Destination | undefined;

  // Dropoff state
  dropoff: DropoffState;
  setDropoffOrderId: (id: string) => void;
  setDropoffTeamId: (id: string) => void;
  setDropoffDriverId: (id: string) => void;
  setDropoffAutoAssign: (autoAssign: boolean) => void;
  setDropoffRecipientId: (id: string | undefined) => void;
  setDropoffRecipientMode: (mode: 'select' | 'create') => void;
  setDropoffDestinationId: (id: string | undefined) => void;
  setDropoffDestinationMode: (mode: 'select' | 'create') => void;
  setDropoffAddress: (field: keyof AddressState, value: string) => void;
  setDropoffRecipient: (field: keyof RecipientState, value: string) => void;
  setDropoffNotes: (notes: string) => void;
  setDropoffTimeWindow: (field: keyof TimeWindowState, value: string) => void;
  setDropoffCompletionRequirements: (field: keyof CompletionRequirementsState, value: boolean) => void;
  handleDropoffAddressSelect: (address: AddressState) => void;

  // Drivers
  drivers: Driver[];
  isLoadingDrivers: boolean;

  // Computed values
  needsPickupSection: boolean;
  needsDropoffSection: boolean;
  isNewRecipient: boolean;
  isNewDestination: boolean;
  selectedRecipient: Recipient | undefined;
  selectedDestination: Destination | undefined;
  selectedOrder: B2BWaveOrderSummary | undefined;

  // Validation
  errors: Record<string, string>;
  validate: () => boolean;
  clearError: (key: string) => void;

  // Form submission
  buildTaskRequest: () => TaskFormData;

  // Constants
  NO_TEAM_VALUE: string;
  NO_ORDER_VALUE: string;
  NO_DRIVER_VALUE: string;
  ORDER_REQUIRED: boolean;
}

const createInitialAddressState = (): AddressState => ({
  search: '',
  streetNumber: '',
  streetAddress: '',
  apartment: '',
  city: '',
  state: '',
  postalCode: '',
});

const createInitialRecipientState = (): RecipientState => ({
  name: '',
  phone: '',
});

const createInitialTimeWindowState = (): TimeWindowState => ({
  completeAfter: '',
  completeBefore: '',
});

export function useTaskForm({
  recipients,
  destinations,
  orders,
  onFetchDriversByTeam,
}: UseTaskFormOptions): UseTaskFormReturn {
  // Task type
  const [taskType, setTaskType] = useState<TaskCreationType>('dropoff');

  // Pickup state - Team/Driver
  const [pickupSelectedTeamId, setPickupSelectedTeamId] = useState<string>(NO_TEAM_VALUE);
  const [pickupSelectedDriverId, setPickupSelectedDriverId] = useState<string>(NO_DRIVER_VALUE);
  const [pickupAutoAssign, setPickupAutoAssign] = useState(false);
  // Pickup state - Recipient SelectOrCreate
  const [pickupSelectedRecipientId, setPickupSelectedRecipientId] = useState<string | undefined>();
  const [pickupRecipientMode, setPickupRecipientMode] = useState<'select' | 'create'>(
    recipients.length > 0 ? 'select' : 'create'
  );
  const [pickupRecipient, setPickupRecipientState] = useState<RecipientState>(createInitialRecipientState());
  // Pickup state - Destination SelectOrCreate
  const [pickupSelectedDestinationId, setPickupSelectedDestinationId] = useState<string | undefined>();
  const [pickupDestinationMode, setPickupDestinationMode] = useState<'select' | 'create'>(
    destinations.length > 0 ? 'select' : 'create'
  );
  const [pickupAddress, setPickupAddressState] = useState<AddressState>(createInitialAddressState());
  // Pickup state - Other
  const [pickupNotes, setPickupNotes] = useState('');
  const [pickupTimeWindow, setPickupTimeWindowState] = useState<TimeWindowState>(createInitialTimeWindowState());
  const [pickupCompletionRequirements, setPickupCompletionRequirementsState] = useState<CompletionRequirementsState>({
    requireSignature: false,
    requirePhoto: true,
    requireNotes: false,
  });
  // Pickup drivers (separate from dropoff)
  const [pickupDrivers, setPickupDrivers] = useState<Driver[]>([]);
  const [isLoadingPickupDrivers, setIsLoadingPickupDrivers] = useState(false);

  // Dropoff state
  const [selectedOrderId, setSelectedOrderId] = useState<string>(NO_ORDER_VALUE);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(NO_TEAM_VALUE);
  const [selectedDriverId, setSelectedDriverId] = useState<string>(NO_DRIVER_VALUE);
  const [autoAssign, setAutoAssign] = useState(false);
  const [selectedRecipientId, setSelectedRecipientId] = useState<string | undefined>();
  const [recipientMode, setRecipientMode] = useState<'select' | 'create'>(
    recipients.length > 0 ? 'select' : 'create'
  );
  const [selectedDestinationId, setSelectedDestinationId] = useState<string | undefined>();
  const [destinationMode, setDestinationMode] = useState<'select' | 'create'>(
    destinations.length > 0 ? 'select' : 'create'
  );
  const [dropoffAddress, setDropoffAddressState] = useState<AddressState>(createInitialAddressState());
  const [dropoffRecipient, setDropoffRecipientState] = useState<RecipientState>(createInitialRecipientState());
  const [dropoffNotes, setDropoffNotes] = useState('');
  const [dropoffTimeWindow, setDropoffTimeWindowState] = useState<TimeWindowState>(createInitialTimeWindowState());
  const [dropoffCompletionRequirements, setDropoffCompletionRequirementsState] = useState<CompletionRequirementsState>({
    requireSignature: true,
    requirePhoto: true,
    requireNotes: false,
  });

  // Drivers
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Computed values
  const needsPickupSection = taskType === 'pickup' || taskType === 'linked';
  const needsDropoffSection = taskType === 'dropoff' || taskType === 'linked';
  // Pickup computed
  const isNewPickupRecipient = pickupRecipientMode === 'create';
  const isNewPickupDestination = pickupDestinationMode === 'create';
  // Dropoff computed
  const isNewRecipient = recipientMode === 'create';
  const isNewDestination = destinationMode === 'create';

  // Pickup selections
  const selectedPickupRecipient = useMemo(
    () => recipients.find(r => r.id === pickupSelectedRecipientId),
    [recipients, pickupSelectedRecipientId]
  );

  const selectedPickupDestination = useMemo(
    () => destinations.find(d => d.id === pickupSelectedDestinationId),
    [destinations, pickupSelectedDestinationId]
  );

  // Dropoff selections
  const selectedRecipient = useMemo(
    () => recipients.find(r => r.id === selectedRecipientId),
    [recipients, selectedRecipientId]
  );

  const selectedDestination = useMemo(
    () => destinations.find(d => d.id === selectedDestinationId),
    [destinations, selectedDestinationId]
  );

  const selectedOrder = useMemo(
    () => orders.find(o => o.id.toString() === selectedOrderId),
    [orders, selectedOrderId]
  );

  // Fetch pickup drivers when pickup team changes
  useEffect(() => {
    if (pickupSelectedTeamId === NO_TEAM_VALUE || !onFetchDriversByTeam) {
      setPickupDrivers([]);
      setPickupSelectedDriverId(NO_DRIVER_VALUE);
      return;
    }

    setIsLoadingPickupDrivers(true);
    setPickupSelectedDriverId(NO_DRIVER_VALUE);

    onFetchDriversByTeam(pickupSelectedTeamId)
      .then((fetchedDrivers) => {
        setPickupDrivers(fetchedDrivers);
      })
      .catch((err) => {
        console.error('Failed to fetch pickup drivers:', err);
        setPickupDrivers([]);
      })
      .finally(() => {
        setIsLoadingPickupDrivers(false);
      });
  }, [pickupSelectedTeamId, onFetchDriversByTeam]);

  // Fetch dropoff drivers when team changes
  useEffect(() => {
    if (selectedTeamId === NO_TEAM_VALUE || !onFetchDriversByTeam) {
      setDrivers([]);
      setSelectedDriverId(NO_DRIVER_VALUE);
      return;
    }

    setIsLoadingDrivers(true);
    setSelectedDriverId(NO_DRIVER_VALUE);

    onFetchDriversByTeam(selectedTeamId)
      .then((fetchedDrivers) => {
        setDrivers(fetchedDrivers);
      })
      .catch((err) => {
        console.error('Failed to fetch drivers:', err);
        setDrivers([]);
      })
      .finally(() => {
        setIsLoadingDrivers(false);
      });
  }, [selectedTeamId, onFetchDriversByTeam]);

  // Pickup setters - Team/Driver
  const setPickupTeamId = useCallback((id: string) => {
    setPickupSelectedTeamId(id);
  }, []);

  const setPickupDriverId = useCallback((id: string) => {
    setPickupSelectedDriverId(id);
  }, []);

  const setPickupAutoAssignFn = useCallback((value: boolean) => {
    setPickupAutoAssign(value);
  }, []);

  // Pickup setters - Recipient SelectOrCreate
  const setPickupRecipientIdFn = useCallback((id: string | undefined) => {
    setPickupSelectedRecipientId(id);
  }, []);

  const setPickupRecipientModeFn = useCallback((mode: 'select' | 'create') => {
    setPickupRecipientMode(mode);
  }, []);

  const setPickupRecipient = useCallback((field: keyof RecipientState, value: string) => {
    setPickupRecipientState(prev => ({ ...prev, [field]: value }));
    if (errors[`pickup.recipient.${field}`]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[`pickup.recipient.${field}`];
        return next;
      });
    }
  }, [errors]);

  // Pickup setters - Destination SelectOrCreate
  const setPickupDestinationIdFn = useCallback((id: string | undefined) => {
    setPickupSelectedDestinationId(id);
  }, []);

  const setPickupDestinationModeFn = useCallback((mode: 'select' | 'create') => {
    setPickupDestinationMode(mode);
  }, []);

  const setPickupAddress = useCallback((field: keyof AddressState, value: string) => {
    setPickupAddressState(prev => ({ ...prev, [field]: value }));
    if (errors[`pickup.${field}`]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[`pickup.${field}`];
        return next;
      });
    }
  }, [errors]);

  const handlePickupAddressSelect = useCallback((address: AddressState) => {
    setPickupAddressState(address);
    setErrors(prev => {
      const next = { ...prev };
      delete next['pickup.streetNumber'];
      delete next['pickup.streetAddress'];
      delete next['pickup.city'];
      return next;
    });
  }, []);

  // Pickup setters - Time/Notes/Completion
  const setPickupTimeWindow = useCallback((field: keyof TimeWindowState, value: string) => {
    setPickupTimeWindowState(prev => ({ ...prev, [field]: value }));
  }, []);

  const setPickupCompletionRequirements = useCallback((field: keyof CompletionRequirementsState, value: boolean) => {
    setPickupCompletionRequirementsState(prev => ({ ...prev, [field]: value }));
  }, []);

  // Dropoff setters
  const setDropoffAddress = useCallback((field: keyof AddressState, value: string) => {
    setDropoffAddressState(prev => ({ ...prev, [field]: value }));
    if (errors[`dropoff.${field}`]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[`dropoff.${field}`];
        return next;
      });
    }
  }, [errors]);

  const setDropoffRecipient = useCallback((field: keyof RecipientState, value: string) => {
    setDropoffRecipientState(prev => ({ ...prev, [field]: value }));
    if (errors[`dropoff.recipient.${field}`]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[`dropoff.recipient.${field}`];
        return next;
      });
    }
  }, [errors]);

  const setDropoffTimeWindow = useCallback((field: keyof TimeWindowState, value: string) => {
    setDropoffTimeWindowState(prev => ({ ...prev, [field]: value }));
  }, []);

  const setDropoffCompletionRequirements = useCallback((field: keyof CompletionRequirementsState, value: boolean) => {
    setDropoffCompletionRequirementsState(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDropoffAddressSelect = useCallback((address: AddressState) => {
    setDropoffAddressState(address);
    setErrors(prev => {
      const next = { ...prev };
      delete next['dropoff.streetNumber'];
      delete next['dropoff.streetAddress'];
      delete next['dropoff.city'];
      return next;
    });
  }, []);

  const clearError = useCallback((key: string) => {
    setErrors(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Validation
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate pickup section
    if (needsPickupSection) {
      // Recipient validation (only when creating new)
      if (isNewPickupRecipient && !pickupRecipient.name.trim()) {
        newErrors['pickup.recipient.name'] = 'Pickup contact name is required';
      }

      // Address validation (only when creating new)
      if (isNewPickupDestination) {
        if (!pickupAddress.streetNumber.trim()) {
          newErrors['pickup.streetNumber'] = 'Street number is required';
        }
        if (!pickupAddress.streetAddress.trim()) {
          newErrors['pickup.streetAddress'] = 'Street name is required';
        }
        if (!pickupAddress.city.trim()) {
          newErrors['pickup.city'] = 'City is required';
        }
      }
    }

    // Validate dropoff section
    if (needsDropoffSection) {
      if (ORDER_REQUIRED && selectedOrderId === NO_ORDER_VALUE) {
        newErrors['dropoff.orderId'] = 'Order selection is required';
      }

      if (isNewRecipient && !dropoffRecipient.name.trim()) {
        newErrors['dropoff.recipient.name'] = 'Recipient name is required';
      }

      if (isNewDestination) {
        if (!dropoffAddress.streetNumber.trim()) {
          newErrors['dropoff.streetNumber'] = 'Street number is required';
        }
        if (!dropoffAddress.streetAddress.trim()) {
          newErrors['dropoff.streetAddress'] = 'Street name is required';
        }
        if (!dropoffAddress.city.trim()) {
          newErrors['dropoff.city'] = 'City is required';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    needsPickupSection,
    needsDropoffSection,
    isNewPickupRecipient,
    isNewPickupDestination,
    pickupRecipient.name,
    pickupAddress,
    selectedOrderId,
    isNewRecipient,
    dropoffRecipient.name,
    isNewDestination,
    dropoffAddress,
  ]);

  // Build task request
  const buildTaskRequest = useCallback((): TaskFormData => {
    const pickupCompletionReqs =
      pickupCompletionRequirements.requireSignature ||
      pickupCompletionRequirements.requirePhoto ||
      pickupCompletionRequirements.requireNotes
        ? {
            signature: pickupCompletionRequirements.requireSignature || undefined,
            photo: pickupCompletionRequirements.requirePhoto || undefined,
            notes: pickupCompletionRequirements.requireNotes || undefined,
          }
        : undefined;

    const dropoffCompletionReqs =
      dropoffCompletionRequirements.requireSignature ||
      dropoffCompletionRequirements.requirePhoto ||
      dropoffCompletionRequirements.requireNotes
        ? {
            signature: dropoffCompletionRequirements.requireSignature || undefined,
            photo: dropoffCompletionRequirements.requirePhoto || undefined,
            notes: dropoffCompletionRequirements.requireNotes || undefined,
          }
        : undefined;

    // Determine pickup recipient info
    let pickupRecipientName: string;
    let pickupRecipientPhone: string | undefined;

    if (isNewPickupRecipient) {
      pickupRecipientName = pickupRecipient.name.trim();
      pickupRecipientPhone = pickupRecipient.phone.replace(/\D/g, '') || undefined;
    } else if (selectedPickupRecipient) {
      pickupRecipientName = selectedPickupRecipient.name;
      pickupRecipientPhone = selectedPickupRecipient.phone || undefined;
    } else {
      pickupRecipientName = pickupRecipient.name.trim();
      pickupRecipientPhone = pickupRecipient.phone.replace(/\D/g, '') || undefined;
    }

    // Determine pickup address
    let pickupAddressData: CreateTaskInput['address'];

    if (isNewPickupDestination) {
      pickupAddressData = {
        number: pickupAddress.streetNumber.trim() || '',
        street: pickupAddress.streetAddress.trim(),
        apartment: pickupAddress.apartment.trim() || undefined,
        city: pickupAddress.city.trim(),
        state: pickupAddress.state.trim().toUpperCase(),
        postalCode: pickupAddress.postalCode.trim(),
        country: 'USA',
      };
    } else if (selectedPickupDestination) {
      pickupAddressData = {
        number: selectedPickupDestination.address?.number || '',
        street: selectedPickupDestination.address?.street || '',
        apartment: selectedPickupDestination.address?.apartment || undefined,
        city: selectedPickupDestination.address?.city || '',
        state: selectedPickupDestination.address?.state || '',
        postalCode: selectedPickupDestination.address?.postalCode || '',
        country: selectedPickupDestination.address?.country || 'USA',
      };
    } else {
      pickupAddressData = {
        number: pickupAddress.streetNumber.trim() || '',
        street: pickupAddress.streetAddress.trim(),
        apartment: pickupAddress.apartment.trim() || undefined,
        city: pickupAddress.city.trim(),
        state: pickupAddress.state.trim().toUpperCase(),
        postalCode: pickupAddress.postalCode.trim(),
        country: 'USA',
      };
    }

    // Determine dropoff recipient info
    let finalRecipientName: string;
    let finalRecipientPhone: string | undefined;

    if (isNewRecipient) {
      finalRecipientName = dropoffRecipient.name.trim();
      finalRecipientPhone = dropoffRecipient.phone.replace(/\D/g, '') || undefined;
    } else if (selectedRecipient) {
      finalRecipientName = selectedRecipient.name;
      finalRecipientPhone = selectedRecipient.phone || undefined;
    } else {
      finalRecipientName = dropoffRecipient.name.trim();
      finalRecipientPhone = dropoffRecipient.phone.replace(/\D/g, '') || undefined;
    }

    // Determine dropoff address
    let dropoffAddressData: CreateTaskInput['address'];

    if (isNewDestination) {
      dropoffAddressData = {
        number: dropoffAddress.streetNumber.trim() || '',
        street: dropoffAddress.streetAddress.trim(),
        apartment: dropoffAddress.apartment.trim() || undefined,
        city: dropoffAddress.city.trim(),
        state: dropoffAddress.state.trim().toUpperCase(),
        postalCode: dropoffAddress.postalCode.trim(),
        country: 'USA',
      };
    } else if (selectedDestination) {
      dropoffAddressData = {
        number: selectedDestination.address?.number || '',
        street: selectedDestination.address?.street || '',
        apartment: selectedDestination.address?.apartment || undefined,
        city: selectedDestination.address?.city || '',
        state: selectedDestination.address?.state || '',
        postalCode: selectedDestination.address?.postalCode || '',
        country: selectedDestination.address?.country || 'USA',
      };
    } else {
      dropoffAddressData = {
        number: dropoffAddress.streetNumber.trim() || '',
        street: dropoffAddress.streetAddress.trim(),
        apartment: dropoffAddress.apartment.trim() || undefined,
        city: dropoffAddress.city.trim(),
        state: dropoffAddress.state.trim().toUpperCase(),
        postalCode: dropoffAddress.postalCode.trim(),
        country: 'USA',
      };
    }

    // Pickup team/driver (for standalone pickup)
    const pickupTeamId = pickupSelectedTeamId === NO_TEAM_VALUE ? undefined : pickupSelectedTeamId;
    const pickupDriverIdValue = pickupSelectedDriverId === NO_DRIVER_VALUE ? undefined : pickupSelectedDriverId;

    // Dropoff team/driver
    const teamId = selectedTeamId === NO_TEAM_VALUE ? undefined : selectedTeamId;
    const driverId = selectedDriverId === NO_DRIVER_VALUE ? undefined : selectedDriverId;

    // Linked tasks - use dropoff's team/driver as the shared assignment
    if (taskType === 'linked') {
      return {
        type: 'linked',
        data: {
          pickup: {
            address: pickupAddressData,
            recipientName: pickupRecipientName,
            recipientPhone: pickupRecipientPhone,
            notes: pickupNotes.trim() || undefined,
            completeAfter: pickupTimeWindow.completeAfter
              ? new Date(pickupTimeWindow.completeAfter).toISOString()
              : undefined,
            completeBefore: pickupTimeWindow.completeBefore
              ? new Date(pickupTimeWindow.completeBefore).toISOString()
              : undefined,
            completionRequirements: pickupCompletionReqs,
          },
          dropoff: {
            orderId: selectedOrderId !== NO_ORDER_VALUE ? parseInt(selectedOrderId, 10) : undefined,
            address: dropoffAddressData,
            recipientName: finalRecipientName,
            recipientPhone: finalRecipientPhone,
            notes: dropoffNotes.trim() || undefined,
            completeAfter: dropoffTimeWindow.completeAfter
              ? new Date(dropoffTimeWindow.completeAfter).toISOString()
              : undefined,
            completeBefore: dropoffTimeWindow.completeBefore
              ? new Date(dropoffTimeWindow.completeBefore).toISOString()
              : undefined,
            completionRequirements: dropoffCompletionReqs,
          },
          teamId,
          driverId,
          autoAssign: autoAssign || undefined,
        },
      };
    }

    // Pickup task (standalone)
    if (taskType === 'pickup') {
      return {
        type: 'single',
        data: {
          taskType: 'pickup',
          teamId: pickupTeamId,
          driverId: pickupDriverIdValue,
          autoAssign: pickupAutoAssign || undefined,
          recipientName: pickupRecipientName,
          recipientPhone: pickupRecipientPhone,
          address: pickupAddressData,
          notes: pickupNotes.trim() || undefined,
          completeAfter: pickupTimeWindow.completeAfter
            ? new Date(pickupTimeWindow.completeAfter).toISOString()
            : undefined,
          completeBefore: pickupTimeWindow.completeBefore
            ? new Date(pickupTimeWindow.completeBefore).toISOString()
            : undefined,
          completionRequirements: pickupCompletionReqs,
        },
      };
    }

    // Dropoff task
    return {
      type: 'single',
      data: {
        taskType: 'dropoff',
        orderId: selectedOrderId !== NO_ORDER_VALUE ? parseInt(selectedOrderId, 10) : undefined,
        teamId,
        driverId,
        autoAssign: autoAssign || undefined,
        recipientName: finalRecipientName,
        recipientPhone: finalRecipientPhone,
        address: dropoffAddressData,
        notes: dropoffNotes.trim() || undefined,
        completeAfter: dropoffTimeWindow.completeAfter
          ? new Date(dropoffTimeWindow.completeAfter).toISOString()
          : undefined,
        completeBefore: dropoffTimeWindow.completeBefore
          ? new Date(dropoffTimeWindow.completeBefore).toISOString()
          : undefined,
        completionRequirements: dropoffCompletionReqs,
      },
    };
  }, [
    taskType,
    // Pickup
    pickupSelectedTeamId,
    pickupSelectedDriverId,
    pickupAutoAssign,
    pickupAddress,
    pickupRecipient,
    pickupNotes,
    pickupTimeWindow,
    pickupCompletionRequirements,
    isNewPickupRecipient,
    isNewPickupDestination,
    selectedPickupRecipient,
    selectedPickupDestination,
    // Dropoff
    dropoffAddress,
    dropoffRecipient,
    dropoffNotes,
    dropoffTimeWindow,
    dropoffCompletionRequirements,
    selectedOrderId,
    selectedTeamId,
    selectedDriverId,
    autoAssign,
    isNewRecipient,
    isNewDestination,
    selectedRecipient,
    selectedDestination,
  ]);

  // Compose pickup state
  const pickup: PickupState = {
    selectedTeamId: pickupSelectedTeamId,
    selectedDriverId: pickupSelectedDriverId,
    autoAssign: pickupAutoAssign,
    selectedRecipientId: pickupSelectedRecipientId,
    recipientMode: pickupRecipientMode,
    recipient: pickupRecipient,
    selectedDestinationId: pickupSelectedDestinationId,
    destinationMode: pickupDestinationMode,
    address: pickupAddress,
    notes: pickupNotes,
    timeWindow: pickupTimeWindow,
    completionRequirements: pickupCompletionRequirements,
  };

  // Compose dropoff state
  const dropoff: DropoffState = {
    selectedOrderId,
    selectedTeamId,
    selectedDriverId,
    autoAssign,
    selectedRecipientId,
    recipientMode,
    selectedDestinationId,
    destinationMode,
    address: dropoffAddress,
    recipient: dropoffRecipient,
    notes: dropoffNotes,
    timeWindow: dropoffTimeWindow,
    completionRequirements: dropoffCompletionRequirements,
  };

  return {
    // Task type
    taskType,
    setTaskType,

    // Pickup
    pickup,
    setPickupTeamId,
    setPickupDriverId,
    setPickupAutoAssign: setPickupAutoAssignFn,
    setPickupRecipientId: setPickupRecipientIdFn,
    setPickupRecipientMode: setPickupRecipientModeFn,
    setPickupRecipient,
    setPickupDestinationId: setPickupDestinationIdFn,
    setPickupDestinationMode: setPickupDestinationModeFn,
    setPickupAddress,
    handlePickupAddressSelect,
    setPickupNotes,
    setPickupTimeWindow,
    setPickupCompletionRequirements,
    // Pickup computed
    pickupDrivers,
    isLoadingPickupDrivers,
    isNewPickupRecipient,
    isNewPickupDestination,
    selectedPickupRecipient,
    selectedPickupDestination,

    // Dropoff
    dropoff,
    setDropoffOrderId: setSelectedOrderId,
    setDropoffTeamId: setSelectedTeamId,
    setDropoffDriverId: setSelectedDriverId,
    setDropoffAutoAssign: setAutoAssign,
    setDropoffRecipientId: setSelectedRecipientId,
    setDropoffRecipientMode: setRecipientMode,
    setDropoffDestinationId: setSelectedDestinationId,
    setDropoffDestinationMode: setDestinationMode,
    setDropoffAddress,
    setDropoffRecipient,
    setDropoffNotes,
    setDropoffTimeWindow,
    setDropoffCompletionRequirements,
    handleDropoffAddressSelect,

    // Drivers
    drivers,
    isLoadingDrivers,

    // Computed
    needsPickupSection,
    needsDropoffSection,
    isNewRecipient,
    isNewDestination,
    selectedRecipient,
    selectedDestination,
    selectedOrder,

    // Validation
    errors,
    validate,
    clearError,

    // Submit
    buildTaskRequest,

    // Constants
    NO_TEAM_VALUE,
    NO_ORDER_VALUE,
    NO_DRIVER_VALUE,
    ORDER_REQUIRED,
  };
}
